if (!customElements.get('cart-gift-promotion')) {
  customElements.define(
    'cart-gift-promotion',
    class CartGiftPromotion extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';

        this.campaignId = this.dataset.campaignId || 'free-gift';
        this.enabled = this.dataset.enabled === 'true';
        this.pageHandles = (this.dataset.pageHandles || '')
          .split(',')
          .map((handle) => handle.trim())
          .filter(Boolean);
        this.qualifyingScope = this.dataset.qualifyingScope || 'all';
        this.requirementType = this.dataset.requirementType || 'amount';
        this.minimumAmount = Number.parseInt(this.dataset.minimumAmount || '0', 10);
        this.minimumQuantity = Number.parseInt(this.dataset.minimumQuantity || '1', 10);
        this.singleBehavior = this.dataset.singleBehavior || 'claim';
        this.discountMethod = this.dataset.discountMethod || 'automatic';
        this.discountCode = (this.dataset.discountCode || '').trim();
        this.requireZeroPrice = this.dataset.requireZeroPrice !== 'false';
        this.fastSelection = this.dataset.fastSelection === 'true';
        this.requireSelection = this.dataset.requireSelection === 'true';
        this.selectionRequiredError =
          this.dataset.selectionRequiredError || 'Please choose your complimentary gift before checking out.';
        this.orderAttributeLabel = this.dataset.orderAttributeLabel || 'Complimentary gift';
        this.orderNoteLabel = this.dataset.orderNoteLabel || 'Complimentary gift (FREE)';
        this.freeLabel = this.dataset.freeLabel || 'FREE';
        this.discountError =
          this.dataset.discountError || 'The gift discount is unavailable. Please contact us before checking out.';
        this.legacyGiftKey = (this.dataset.legacyGiftKey || '').trim();

        this.select = this.querySelector('[data-promotion-select]');
        this.claimButton = this.querySelector('[data-promotion-claim]');
        this.error = this.querySelector('[data-promotion-error]');
        this.preview = this.querySelector('[data-promotion-preview]');
        this.previewImage = this.querySelector('[data-promotion-preview-image]');
        this.previewTitle = this.querySelector('[data-promotion-preview-title]');
        this.status = this.querySelector('[data-promotion-status]');
        this.checkoutButton = document.getElementById('CartDrawer-Checkout');
        this.cartForm = document.getElementById('CartDrawer-Form');
        this.checkoutGuardEnabled = this.enabled && this.requireSelection && this.isScopedPage();
        this.isSyncing = this.checkoutGuardEnabled;
        this.isUpdating = false;
        this.qualifiers = Array.from(this.querySelectorAll('[data-promotion-qualifier]')).map((element) => ({
          productId: String(element.dataset.productId || ''),
          variantId: String(element.dataset.variantId || ''),
        }));
        this.rewards = this.readRewards();
        this.originalClaimLabel = this.claimButton ? this.claimButton.textContent.trim() : '';

        this.onSelectChange = this.handleSelectChange.bind(this);
        this.onClaim = this.handleClaim.bind(this);
        this.onCheckoutAttempt = this.handleCheckoutAttempt.bind(this);
        if (this.select) this.select.addEventListener('change', this.onSelectChange);
        if (this.claimButton) this.claimButton.addEventListener('click', this.onClaim);
        if (this.checkoutGuardEnabled && this.checkoutButton) {
          this.checkoutButton.addEventListener('click', this.onCheckoutAttempt, true);
        }
        if (this.checkoutGuardEnabled && this.cartForm) {
          this.cartForm.addEventListener('submit', this.onCheckoutAttempt, true);
        }
        this.updateCheckoutAvailability();

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event?.source === 'cart-gift-promotion') return;
            window.setTimeout(() => {
              const current = document.querySelector(
                `cart-gift-promotion[data-campaign-id="${CSS.escape(this.campaignId)}"]`
              );
              if (current) current.sync();
            }, 120);
          });
        }

        this.sync();
      }

      disconnectedCallback() {
        if (this.select && this.onSelectChange) this.select.removeEventListener('change', this.onSelectChange);
        if (this.claimButton && this.onClaim) this.claimButton.removeEventListener('click', this.onClaim);
        if (this.checkoutButton && this.onCheckoutAttempt) {
          this.checkoutButton.removeEventListener('click', this.onCheckoutAttempt, true);
        }
        if (this.cartForm && this.onCheckoutAttempt) {
          this.cartForm.removeEventListener('submit', this.onCheckoutAttempt, true);
        }
        if (this.unsubscribe) this.unsubscribe();
      }

      readRewards() {
        if (this.select) {
          return Array.from(this.select.querySelectorAll('[data-promotion-reward]')).map((option) => ({
            variantId: String(option.value),
            label: option.dataset.label || option.textContent.trim(),
            image: option.dataset.image || '',
            handle: option.dataset.productHandle || '',
            option,
          }));
        }

        const reward = this.querySelector('[data-promotion-reward]');
        if (!reward) return [];
        return [
          {
            variantId: String(reward.dataset.variantId || ''),
            label: reward.dataset.label || '',
            image: reward.dataset.image || '',
            handle: reward.dataset.productHandle || '',
            option: reward,
          },
        ];
      }

      isScopedPage() {
        if (!this.pageHandles.length) return true;
        const match = window.location.pathname.match(/^\/pages\/([^/?#]+)/);
        return Boolean(match && this.pageHandles.includes(match[1]));
      }

      route(path) {
        const root = window.Shopify?.routes?.root || '/';
        return root + path.replace(/^\//, '');
      }

      async request(path, options = {}) {
        const response = await fetch(this.route(path), {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            ...(options.body ? { 'Content-Type': 'application/json' } : {}),
            ...(options.headers || {}),
          },
          ...options,
        });

        if (response.ok) return response;
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Unable to update the complimentary gift.');
      }

      async getCart() {
        if (!window.__cartGiftPromotionCartRequest) {
          window.__cartGiftPromotionCartRequest = this.request('cart.js')
            .then((response) => response.json())
            .finally(() => {
              window.__cartGiftPromotionCartRequest = null;
            });
        }
        return window.__cartGiftPromotionCartRequest;
      }

      isPromotionGift(item) {
        const properties = item.properties || {};
        if (properties._cart_promotion_id === this.campaignId) return true;
        return Boolean(this.legacyGiftKey && properties[this.legacyGiftKey]);
      }

      giftLines(cart) {
        return cart.items.filter((item) => this.isPromotionGift(item));
      }

      paidItems(cart) {
        return cart.items.filter((item) => !item.properties?._cart_promotion_id && !this.isPromotionGift(item));
      }

      qualifyingItems(cart) {
        const paidItems = this.paidItems(cart);
        if (this.qualifyingScope !== 'products') return paidItems;
        if (!this.qualifiers.length) return [];

        return paidItems.filter((item) =>
          this.qualifiers.some((qualifier) => {
            if (String(item.product_id) !== qualifier.productId) return false;
            return !qualifier.variantId || String(item.variant_id) === qualifier.variantId;
          })
        );
      }

      isEligible(cart) {
        const items = this.qualifyingItems(cart);
        if (this.requirementType === 'quantity') {
          const quantity = items.reduce((total, item) => total + item.quantity, 0);
          return quantity >= this.minimumQuantity;
        }

        const amount = items.reduce((total, item) => total + item.final_line_price, 0);
        return amount >= this.minimumAmount;
      }

      rewardForVariant(variantId) {
        return this.rewards.find((reward) => reward.variantId === String(variantId)) || null;
      }

      rewardForLabel(label) {
        return this.rewards.find((reward) => reward.label === label) || null;
      }

      selectedReward() {
        if (this.select) return this.rewardForVariant(this.select.value);
        return this.rewards[0] || null;
      }

      setSelectedReward(reward, hasGift = false) {
        if (this.select) this.select.value = reward?.variantId || '';
        this.showPreview(this.select ? reward : null);

        if (this.claimButton) {
          this.claimButton.disabled = hasGift;
          this.claimButton.textContent = hasGift ? this.freeLabel : this.originalClaimLabel;
        }
      }

      showPreview(reward) {
        if (!this.preview) return;
        this.preview.hidden = !reward;
        if (!reward) return;

        if (this.previewTitle) this.previewTitle.textContent = reward.label;
        if (this.previewImage) {
          this.previewImage.alt = reward.label;
          this.previewImage.hidden = !reward.image;
          if (reward.image) this.previewImage.src = reward.image;
        }
      }

      setBusy(isBusy) {
        this.isUpdating = isBusy;
        this.toggleAttribute('aria-busy', isBusy);
        if (this.select) this.select.disabled = isBusy;
        if (this.claimButton) this.claimButton.disabled = isBusy;
        if (this.status) this.status.hidden = !isBusy;
        this.updateCheckoutAvailability();
      }

      setError(message) {
        if (!this.error) return;
        this.error.textContent = message || '';
        this.error.hidden = !message;
        const selectionControl = this.select || this.claimButton;
        if (selectionControl) selectionControl.toggleAttribute('aria-invalid', Boolean(message));
      }

      updateCheckoutAvailability() {
        if (!this.checkoutGuardEnabled || !this.checkoutButton) return;
        this.checkoutButton.disabled = this.isSyncing || this.isUpdating || !this.currentCart;
      }

      hasValidGiftSelection(cart) {
        if (!cart) return false;
        if (!this.isEligible(cart)) return true;
        const gifts = this.giftLines(cart);
        if (gifts.length !== 1 || gifts[0].quantity !== 1) return false;
        return Boolean(this.rewardForVariant(gifts[0].variant_id) && this.giftIsFree(gifts[0]));
      }

      handleCheckoutAttempt(event) {
        if (!this.checkoutGuardEnabled || this.hasValidGiftSelection(this.currentCart)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.setError(this.selectionRequiredError);

        const selectionControl = this.select || this.claimButton;
        if (!selectionControl) return;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        selectionControl.focus({ preventScroll: true });
      }

      drawerHasGiftLine() {
        return Boolean(
          document.querySelector(
            `[data-cart-promotion-id="${CSS.escape(this.campaignId)}"], [data-legacy-promotion-campaign="${CSS.escape(this.campaignId)}"]`
          )
        );
      }

      sectionRequestData() {
        return {
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        };
      }

      async removeGifts(lines) {
        if (!lines.length) return null;
        const updates = {};
        lines.forEach((item) => {
          updates[item.key] = 0;
        });
        const response = await this.request('cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ updates }),
        });
        return response.json();
      }

      async addGift(reward) {
        const response = await this.request('cart/add.js', {
          method: 'POST',
          body: JSON.stringify({
            items: [
              {
                id: reward.variantId,
                quantity: 1,
                properties: {
                  _cart_promotion_id: this.campaignId,
                  _cart_promotion_reward: reward.label,
                  _cart_promotion_badge: this.freeLabel,
                  [this.orderAttributeLabel]: this.freeLabel,
                },
              },
            ],
          }),
        });
        return response.json();
      }

      async applyDiscountCode() {
        if (this.discountMethod !== 'code' || !this.discountCode) return;
        const redirect = this.route('cart.js');
        const response = await fetch(
          this.route(`discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(redirect)}`),
          { credentials: 'same-origin' }
        );
        if (!response.ok) throw new Error(this.discountError);
      }

      async persistSelection(cart, reward, includeSections = false) {
        const marker = `${this.orderNoteLabel}:`;
        const noteLines = (cart.note || '')
          .split(/\r?\n/)
          .filter((line) => !line.trim().startsWith(marker));
        const label = reward?.label || '';
        if (label) noteLines.push(`${marker} ${label}`);

        const response = await this.request('cart/update.js', {
          method: 'POST',
          body: JSON.stringify({
            attributes: { [this.orderAttributeLabel]: label },
            note: noteLines.join('\n').trim(),
            ...(includeSections ? this.sectionRequestData() : {}),
          }),
        });
        return response.json();
      }

      giftIsFree(item) {
        return !this.requireZeroPrice || item.final_line_price === 0;
      }

      async refreshDrawer(shouldOpen = true) {
        const maximumAttempts = 2;

        for (let attempt = 1; attempt <= maximumAttempts; attempt += 1) {
          try {
            const response = await fetch(this.route('cart?sections=cart-drawer,cart-icon-bubble'), {
              credentials: 'same-origin',
            });
            if (!response.ok) throw new Error('Unable to load the updated cart sections.');

            const sections = await response.json();
            const drawerDocument = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
            const bubbleDocument = new DOMParser().parseFromString(sections['cart-icon-bubble'] || '', 'text/html');
            const cartDrawer = document.querySelector('cart-drawer');
            const currentDrawer = cartDrawer?.querySelector('#CartDrawer');
            const updatedDrawer = drawerDocument.querySelector('#CartDrawer');
            const currentBubble = document.querySelector('#cart-icon-bubble');
            const updatedBubble = bubbleDocument.querySelector('#cart-icon-bubble');

            if (!cartDrawer || !currentDrawer || !updatedDrawer) {
              throw new Error('Unable to refresh the cart drawer.');
            }

            currentDrawer.innerHTML = updatedDrawer.innerHTML;
            cartDrawer.classList.toggle('is-empty', !currentDrawer.querySelector('.cart-item'));
            if (currentBubble && updatedBubble) currentBubble.innerHTML = updatedBubble.innerHTML;

            if (shouldOpen && typeof cartDrawer.open === 'function') {
              const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
              cartDrawer.open();
            }
            return;
          } catch (error) {
            if (attempt === maximumAttempts) throw error;
            await new Promise((resolve) => window.setTimeout(resolve, 150));
          }
        }
      }

      renderFastUpdate(cartState) {
        const drawerHtml = cartState?.sections?.['cart-drawer'];
        const bubbleHtml = cartState?.sections?.['cart-icon-bubble'];
        if (!drawerHtml) return false;

        const drawerDocument = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const replacements = ['.drawer__cart-items-wrapper', '.cart-drawer__footer'];

        for (const selector of replacements) {
          const currentElement = document.querySelector(`#CartDrawer ${selector}`);
          const updatedElement = drawerDocument.querySelector(`#CartDrawer ${selector}`);
          if (!currentElement || !updatedElement) return false;
          currentElement.replaceWith(updatedElement);
        }

        if (bubbleHtml) {
          const bubbleDocument = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const currentBubble = document.getElementById('cart-icon-bubble');
          const updatedBubble = bubbleDocument.getElementById('cart-icon-bubble');
          if (currentBubble && updatedBubble) currentBubble.innerHTML = updatedBubble.innerHTML;
        }

        return true;
      }

      sync() {
        if (this.syncPromise) {
          this.syncAgain = true;
          return this.syncPromise;
        }

        this.syncPromise = this.performSync()
          .catch((error) => this.setError(error.message))
          .finally(() => {
            this.syncPromise = null;
            if (this.syncAgain) {
              this.syncAgain = false;
              this.sync();
              return;
            }
            this.isSyncing = false;
            this.updateCheckoutAvailability();
          });
        return this.syncPromise;
      }

      async performSync() {
        const cart = await this.getCart();
        if (this.fastSelection || this.checkoutGuardEnabled) this.currentCart = cart;
        const gifts = this.giftLines(cart);
        const savedLabel = cart.attributes?.[this.orderAttributeLabel] || '';

        if (!this.enabled) {
          this.hidden = true;
          if (!gifts.length && !savedLabel) return;
          await this.removeGifts(gifts);
          const latestCart = await this.getCart();
          await this.persistSelection(latestCart, null);
          await this.refreshDrawer(false);
          return;
        }

        if (!this.isScopedPage()) {
          this.hidden = true;
          const invalidGift = gifts.some((item) => !this.rewardForVariant(item.variant_id) || !this.giftIsFree(item));
          if (gifts.length && (!this.isEligible(cart) || invalidGift)) {
            await this.removeGifts(gifts);
            const latestCart = await this.getCart();
            await this.persistSelection(latestCart, null);
            await this.refreshDrawer(false);
          }
          return;
        }

        const eligible = this.isEligible(cart);

        if (!eligible) {
          this.hidden = true;
          this.setSelectedReward(null);
          if (!gifts.length && !savedLabel) return;
          await this.removeGifts(gifts);
          const latestCart = await this.getCart();
          await this.persistSelection(latestCart, null);
          await this.refreshDrawer(false);
          return;
        }

        this.hidden = false;
        if (gifts.length) {
          const reward = this.rewardForVariant(gifts[0].variant_id);
          if (!reward || gifts.length > 1 || gifts[0].quantity !== 1) {
            await this.removeGifts(gifts);
            const latestCart = await this.getCart();
            await this.persistSelection(latestCart, null);
            await this.refreshDrawer(false);
            return;
          }

          if (!this.giftIsFree(gifts[0])) {
            await this.applyDiscountCode();
            const discountedCart = await this.getCart();
            const discountedGift = this.giftLines(discountedCart)[0];
            if (!discountedGift || !this.giftIsFree(discountedGift)) {
              await this.removeGifts(this.giftLines(discountedCart));
              const latestCart = await this.getCart();
              await this.persistSelection(latestCart, null);
              throw new Error(this.discountError);
            }
          }

          this.setSelectedReward(reward, true);
          if (savedLabel !== reward.label) await this.persistSelection(cart, reward);
          if (!this.drawerHasGiftLine()) await this.refreshDrawer(false);
          return;
        }

        const savedReward = this.rewardForLabel(savedLabel);
        if (savedReward) {
          await this.chooseReward(savedReward, { openDrawer: false });
          return;
        }

        if (savedLabel) await this.persistSelection(cart, null);
        if (this.rewards.length === 1 && this.singleBehavior === 'automatic') {
          await this.chooseReward(this.rewards[0], { openDrawer: false });
        }
      }

      async chooseReward(reward, { openDrawer = true } = {}) {
        if (!reward) return;
        this.setBusy(true);
        this.setError('');

        try {
          const cart = this.fastSelection && this.currentCart ? this.currentCart : await this.getCart();
          if (!this.isEligible(cart)) throw new Error('Your cart no longer qualifies for this complimentary gift.');

          await this.removeGifts(this.giftLines(cart));
          await this.addGift(reward);
          await this.applyDiscountCode();

          const updatedCart = this.fastSelection
            ? await this.persistSelection(cart, reward, true)
            : await this.getCart();
          const gift = this.giftLines(updatedCart).find(
            (item) => String(item.variant_id) === reward.variantId
          );
          if (!gift || !this.giftIsFree(gift)) {
            await this.removeGifts(this.giftLines(updatedCart));
            const cleanCart = await this.getCart();
            const resetCart = await this.persistSelection(cleanCart, null, this.fastSelection);
            if (this.fastSelection) {
              this.currentCart = resetCart;
              this.renderFastUpdate(resetCart);
            }
            throw new Error(this.discountError);
          }

          if (this.fastSelection) {
            this.currentCart = updatedCart;
            if (!this.renderFastUpdate(updatedCart)) await this.refreshDrawer(openDrawer);
          } else {
            await this.persistSelection(updatedCart, reward);
            await this.refreshDrawer(openDrawer);
          }
        } catch (error) {
          this.setError(error.message || 'Unable to add this complimentary gift.');
        } finally {
          this.setBusy(false);
        }
      }

      async clearReward() {
        this.setBusy(true);
        this.setError('');
        try {
          const cart = this.fastSelection && this.currentCart ? this.currentCart : await this.getCart();
          const removedState = await this.removeGifts(this.giftLines(cart));

          if (this.fastSelection) {
            const updatedCart = await this.persistSelection(removedState || cart, null, true);
            this.currentCart = updatedCart;
            if (!this.renderFastUpdate(updatedCart)) await this.refreshDrawer(true);
          } else {
            const updatedCart = await this.getCart();
            await this.persistSelection(updatedCart, null);
            await this.refreshDrawer(true);
          }
        } catch (error) {
          this.setError(error.message || 'Unable to remove this complimentary gift.');
        } finally {
          this.setBusy(false);
        }
      }

      handleSelectChange(event) {
        event.stopPropagation();
        const reward = this.selectedReward();
        this.showPreview(reward);
        if (reward) {
          this.chooseReward(reward);
        } else {
          this.clearReward();
        }
      }

      handleClaim(event) {
        event.preventDefault();
        const reward = this.selectedReward();
        if (reward) this.chooseReward(reward);
      }
    }
  );
}
